import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { WalletReport } from "@/components/prepaid/WalletReport";
import { PurchasingReport } from "@/components/suppliers/PurchasingReport";
import DepartmentReport from "@/components/fashion/DepartmentReport";
import ProductSalesReport from "@/components/reports/ProductSalesReport";
import DateRangeQuickPicker from "@/components/reports/DateRangeQuickPicker";
import CustomReportBuilder from "@/components/reports/CustomReportBuilder";
import EmailReportDialog from "@/components/reports/EmailReportDialog";
import GetReportButton from "@/components/reports/GetReportButton";
import { toast } from "sonner";
import { walletName } from "@/lib/wallet";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { printDoc, pmoney } from "@/lib/print";
import { downloadCustomReportCsv } from "@/lib/customReportExport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, PieChart, Pie, CartesianGrid, ReferenceLine } from "recharts";
import { TrendingUp, DollarSign, PiggyBank, Boxes, Package, FileBarChart } from "lucide-react";
import AccessRestricted from "@/components/AccessRestricted";

const PIE = ["#10b981", "#0ea5e9", "#f59e0b", "#8b5cf6", "#f43f5e", "#14b8a6"];
// R1-03: clearer negative display for Operating Profit/Loss — same number as money(), sign moved
// in front of the currency code instead of after it (e.g. "-JMD $453,345.00" not "JMD $-453,345.00").
const signedMoney = (n) => (Number(n || 0) < 0 ? `-${money(Math.abs(n))}` : money(n));
const trunc = (s, n) => { const t = String(s || ""); return t.length > n ? t.slice(0, Math.max(1, n - 1)).trimEnd() + "…" : t; };
function useLabelWidth() {
  const calc = () => (window.innerWidth < 640 ? [112, 14] : window.innerWidth < 1280 ? [150, 19] : [190, 24]);
  const [v, setV] = useState(calc);
  useEffect(() => { const h = () => setV(calc()); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return v;
}

export default function Reports() {
  const { business, user } = useAuth();
  const modules = useModules();
  const canEmail = user?.role === "owner"; // R1-05: Owner-only for V1, no new Manager permission introduced.
  const [params, setParams] = useSearchParams();
  const walletOn = modules.isAvailable("prepaid_accounts");
  // Suppliers Phase 4: Purchasing tab follows the Suppliers module (ON, or OFF with purchasing history → read-only).
  const purchasingOn = modules.isAvailable("suppliers");
  const fashionOn = !!business?.is_fashion;
  const tab = walletOn && params.get("tab") === "wallet" ? "wallet" : (purchasingOn && params.get("tab") === "purchasing" ? "purchasing" : (fashionOn && params.get("tab") === "departments" ? "departments" : (params.get("tab") === "products" ? "products" : "business")));
  const tabs = [["business", "Business"], ["products", "Products"], ...(walletOn ? [["wallet", walletName(business)]] : []), ...(purchasingOn ? [["purchasing", "Purchasing"]] : []), ...(fashionOn ? [["departments", "Departments"]] : [])];
  const cur = business?.currency || "JMD $";
  const [range, setRange] = useState({ start: new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) });
  const [d, setD] = useState(null);
  const [months, setMonths] = useState(6);
  const [tr, setTr] = useState(null);
  const [labelW, labelChars] = useLabelWidth();
  const [showBuilder, setShowBuilder] = useState(false);
  const [showEmailBusiness, setShowEmailBusiness] = useState(false);
  const [bizBusy, setBizBusy] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  // R1-03: drop a /reports response if a newer request (range changed again, e.g. rapid month-nav
  // clicks) has already started — otherwise an older, slower response could overwrite newer data.
  const loadSeq = useRef(0);
  const load = () => { const seq = ++loadSeq.current; api.get("/reports", { params: range }).then((r) => { if (seq === loadSeq.current) { setD(r.data); setAccessDenied(false); } }).catch((e) => { if (seq === loadSeq.current && e.response?.status === 403) setAccessDenied(true); }); };
  useEffect(() => { api.get("/reports/trends", { params: { months } }).then((r) => setTr(r.data)).catch(() => setTr(null)); }, [months]);
  useEffect(() => { load(); }, [range]);
  // Owner Settings -> Permissions -> "Manager Access to Reports": if this is revoked while a
  // Manager is already sitting on this page, the next data request 403s and we show the same
  // clean restricted screen the route guard shows on fresh navigation — never a raw/broken page.
  if (accessDenied) return <AccessRestricted module="Reports" reason="manager_reports_off" />;
  if (!d) return <p className="text-muted-foreground">Loading…</p>;

  const profitPositive = d.operating_profit >= 0;

  const printReport = () => {
    const kv = (rows) => `<table class="data"><tbody>${rows.map((r) => `<tr><td>${r[0]}</td><td class="right"><strong>${r[1]}</strong></td></tr>`).join("")}</tbody></table>`;
    // R1-04A: each list table now carries a <thead> (column meaning only — no figures/labels touched)
    // so a long table (e.g. many cashiers/categories) repeats its heading if it spans a page break.
    const list = (title, arr, nameCol = "Name") => `<div class="section"><h3>${title}</h3><table class="data"><thead><tr><th>${nameCol}</th><th class="right">Amount</th></tr></thead><tbody>${arr.map((x) => `<tr><td>${x.name}</td><td class="right">${pmoney(x.total ?? x.revenue, cur)}</td></tr>`).join("") || "<tr><td colspan=\"2\">No data</td></tr>"}</tbody></table></div>`;
    const body = `
      <div class="section"><h3>Profit & Loss Summary</h3>${kv([
        ["Total Sales", pmoney(d.sales_total, cur)],
        ["Gross Profit (estimate)", pmoney(d.gross_profit, cur)],
        ["Expenses", pmoney(d.expenses_total, cur)],
        ["Estimated Operating Profit", pmoney(d.operating_profit, cur)],
        ["Payments Collected", pmoney(d.payments_collected, cur)],
        ["Inventory Value", pmoney(d.inventory_value, cur)],
        ["Accounts Receivable", pmoney(d.total_receivable, cur)],
        ["Overdue", pmoney(d.total_overdue, cur)],
      ])}</div>
      ${list("Top-Selling Products", d.top_products, "Product")}
      ${list("Sales by Payment Method (gross tendered)", d.sales_by_method, "Method")}
      ${list("Refunds by Payment Method", d.refunds_by_method || [], "Method")}
      ${list("Sales by Cashier", d.sales_by_cashier, "Cashier")}
      ${list("Expenses by Category", d.expenses_by_category, "Category")}`;
    // plain: true drops receipt-only content (return policy / thank-you footer / bank box) — this is an
    // internal management report, not a customer-facing document. Tenant branding (name/logo/address/etc.)
    // in the header is untouched — it already comes straight from the authenticated business's own settings.
    const footerNote = `Business Report  ·  Period: ${d.start} → ${d.end}  ·  Generated ${new Date().toLocaleString()}`;
    printDoc({ business, docTitle: "Business Report", docRef: "", meta: [["Period", `${d.start} → ${d.end}`]], body, accent: "#8b5cf6", plain: true, pageNumbers: true, footerNote, printButton: true });
  };

  // Reports Final UI Polish: Business tab never had a standalone Download CSV button before — the
  // "Get Report" menu's CSV item is wired to the existing `/reports/custom/csv` endpoint (already
  // shipped in R1-04B/used by Email + Custom Report Builder) scoped to sections=business. No
  // backend change, no new report-generation logic. NOTE for Owner review: this is a newly
  // introduced CSV capability for this tab (Business never had a CSV export before this build).
  const bizCsv = async () => {
    setBizBusy("csv");
    const err = await downloadCustomReportCsv({ start: range.start, end: range.end, sections: "business" }, range);
    if (err) toast.error(err);
    setBizBusy("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold">Reports</h1>
          <p className="text-muted-foreground">Simple, plain-language insights. No accounting degree required.</p>
          {tabs.length > 1 && <div className="flex gap-1 mt-2" data-testid="reports-tabs">
            {tabs.map(([k, l]) => <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setParams(k === "business" ? {} : { tab: k }, { replace: true })} data-testid={`reports-tab-${k}`}>{l}</Button>)}
          </div>}
        </div>
        {tab === "business" && <div className="flex flex-wrap gap-2 items-end">
          <DateRangeQuickPicker value={range} onChange={(start, end) => setRange({ start, end })} testidPrefix="rep-quick" startTestId="rep-start" endTestId="rep-end" />
          <GetReportButton onPrint={printReport} onDownloadCsv={bizCsv} onEmail={() => setShowEmailBusiness(true)} canEmail={canEmail} busy={bizBusy} testidPrefix="biz" />
        </div>}
        {/* R1-04B: a single, always-visible entry point into the Custom Report Builder — lives
            inside the Reports page itself, not as a fifth tab, so it's reachable no matter which
            report tab is currently open. */}
        <Button variant="secondary" onClick={() => setShowBuilder(true)} data-testid="build-custom-report-btn">
          <FileBarChart className="w-4 h-4 mr-1.5" /> Build Custom Report
        </Button>
      </div>
      <CustomReportBuilder open={showBuilder} onOpenChange={setShowBuilder} defaultRange={range} />
      {tab === "business" && <EmailReportDialog open={showEmailBusiness} onOpenChange={setShowEmailBusiness} sections={["business"]} range={range} reportLabel="Business Sales & Profit/Loss" />}
      {tab === "departments" ? <DepartmentReport /> : tab === "purchasing" ? <PurchasingReport business={business} readOnly={!modules.isEnabled("suppliers")} onAccessDenied={() => setAccessDenied(true)} /> : tab === "wallet" ? <WalletReport business={business} onAccessDenied={() => setAccessDenied(true)} /> : tab === "products" ? <ProductSalesReport onAccessDenied={() => setAccessDenied(true)} /> : (<>

      {/* P&L headline */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Big icon={DollarSign} label="Total Sales" value={money(d.sales_total)} color="text-emerald-500" />
        <Big icon={TrendingUp} label="Gross Profit (est.)" value={money(d.gross_profit)} color="text-sky-500" />
        <Big icon={PiggyBank} label="Expenses" value={money(d.expenses_total)} color="text-rose-500" />
        <Card className={`p-5 border-2 ${profitPositive ? "border-emerald-500/40" : "border-rose-500/40"}`}>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Operating Profit (est.)</p>
          <p className={`mono text-2xl font-bold mt-1 ${profitPositive ? "text-emerald-500" : "text-rose-500"}`}>{signedMoney(d.operating_profit)}</p>
          <p className="text-xs text-muted-foreground mt-1">Sales − cost of goods − expenses</p>
        </Card>
      </div>

      {/* Monthly Performance — R1-03: replaces the previous four-line overlapping chart with two
          single-metric bar charts (easier to read at a glance) plus a plain breakdown table. Same
          /reports/trends data & figures — only the presentation changed. The last bucket is always
          the current calendar month (the backend builds buckets ending on "today()"), so it's
          flagged "In Progress" instead of being compared as if it were a finished month. */}
      {tr && (
        <Card className="p-5" data-testid="monthly-performance">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
            <div><p className="font-head font-bold">Monthly Performance</p><p className="text-xs text-muted-foreground">Sales and operating profit/loss, month by month</p></div>
            <div className="flex gap-1">
              {[3, 6, 12].map((m) => <Button key={m} size="sm" variant={months === m ? "default" : "outline"} onClick={() => setMonths(m)} data-testid={`trend-${m}m`}>{m}M</Button>)}
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <div data-testid="monthly-sales-chart">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Monthly Sales</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tr.series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={48} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }} labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }} itemStyle={{ color: "hsl(var(--foreground))" }} />
                  <Bar dataKey="sales" name="Sales" radius={[6, 6, 0, 0]} maxBarSize={44}>
                    {tr.series.map((s, i) => <Cell key={s.month} fill="#10b981" fillOpacity={i === tr.series.length - 1 ? 0.5 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div data-testid="monthly-pl-chart">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Monthly Operating Profit / Loss</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tr.series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={48} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} formatter={(v) => signedMoney(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }} labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }} itemStyle={{ color: "hsl(var(--foreground))" }} />
                  <ReferenceLine y={0} stroke="hsl(var(--border))" />
                  <Bar dataKey="operating_profit" name="Operating Profit/Loss" radius={[6, 6, 0, 0]} maxBarSize={44}>
                    {tr.series.map((s, i) => <Cell key={s.month} fill={s.operating_profit >= 0 ? "#10b981" : "#f43f5e"} fillOpacity={i === tr.series.length - 1 ? 0.5 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="monthly-breakdown-table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="text-left py-2 px-2">Month</th>
                  <th className="text-right py-2 px-2">Sales</th>
                  <th className="text-right py-2 px-2">Expenses</th>
                  <th className="text-right py-2 px-2">Gross Profit</th>
                  <th className="text-right py-2 px-2">Operating Profit/Loss</th>
                </tr>
              </thead>
              <tbody>
                {tr.series.map((s, i) => {
                  const incomplete = i === tr.series.length - 1;
                  return (
                    <tr key={s.month} className="border-b border-border/50" data-testid={`month-row-${s.month}`}>
                      <td className="py-2 px-2 font-medium whitespace-nowrap">{s.label}{incomplete && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-500" data-testid={`month-incomplete-${s.month}`}>In Progress</span>}</td>
                      <td className="py-2 px-2 mono text-right whitespace-nowrap">{money(s.sales)}</td>
                      <td className="py-2 px-2 mono text-right whitespace-nowrap">{money(s.expenses)}</td>
                      <td className="py-2 px-2 mono text-right whitespace-nowrap">{money(s.gross_profit)}</td>
                      <td className={`py-2 px-2 mono text-right font-semibold whitespace-nowrap ${s.operating_profit >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{signedMoney(s.operating_profit)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3" data-testid="monthly-incomplete-note">"In Progress" marks the current calendar month, which isn't finished yet — compare completed months to each other, not against it.</p>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <p className="font-head font-bold mb-1">Top-Selling Products</p>
          <p className="text-xs text-muted-foreground mb-3" data-testid="top-products-caption">Top {Math.min(8, d.top_products.length) || 8} by sales in this period, highest first. Hover or tap a bar for the full name.</p>
          {d.top_products.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="top-products-empty">No product sales in this period.</p>
          ) : (
            <div data-testid="top-products-chart">
              <ResponsiveContainer width="100%" height={Math.max(160, 44 * Math.min(8, d.top_products.length) + 40)}>
                <BarChart data={d.top_products.slice(0, 8)} layout="vertical" margin={{ left: 4, right: 36, top: 4, bottom: 4 }} barCategoryGap={10}>
                  <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="name" width={labelW} interval={0} stroke="hsl(var(--muted-foreground))"
                    tick={({ x, y, payload }) => <text x={x} y={y} dy={4} textAnchor="end" fontSize={12} fill="hsl(var(--foreground))"><title>{payload.value}</title>{trunc(payload.value, labelChars)}</text>} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))", maxWidth: 280, whiteSpace: "normal" }} labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }} itemStyle={{ color: "hsl(var(--foreground))" }}
                    formatter={(v, _n, p) => [`${money(v)}${p?.payload?.qty != null ? ` · ${p.payload.qty} sold` : ""}`, "Sales"]} />
                  <Bar dataKey="revenue" radius={[0, 6, 6, 0]} maxBarSize={28}>{d.top_products.slice(0, 8).map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
        <Card className="p-5">
          <p className="font-head font-bold mb-1">Sales by Payment Method</p>
          <p className="text-xs text-muted-foreground mb-3" data-testid="sales-by-method-basis">Amounts tendered on completed sales in this period (split tenders shown by their parts). Refunds are listed separately below.</p>
          <div className="flex flex-col xl:flex-row items-center gap-4" data-testid="sales-by-method-chart">
            <div className="w-full xl:w-1/2 min-w-0">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={d.sales_by_method} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="none" isAnimationActive={false}>
                    {d.sales_by_method.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [money(v), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }} labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }} itemStyle={{ color: "hsl(var(--foreground))" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="w-full xl:w-1/2 min-w-0 space-y-1.5 text-sm" data-testid="sales-by-method-legend">
              {(() => { const tot = d.sales_by_method.reduce((a, x) => a + x.total, 0) || 1; return d.sales_by_method.map((e, i) => (
                <li key={e.name} className="flex items-center gap-2 min-w-0" data-testid={`method-legend-${e.name}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE[i % PIE.length] }} />
                  <span className="truncate">{e.name}</span>
                  <span className="ml-auto mono text-xs sm:text-sm shrink-0">{money(e.total)}</span>
                  <span className="w-12 text-right text-xs text-muted-foreground shrink-0">{Math.round((e.total / tot) * 100)}%</span>
                </li>)); })()}
              {d.sales_by_method.length === 0 && <li className="text-muted-foreground text-xs" data-testid="method-legend-empty">No completed sales in this period.</li>}
            </ul>
          </div>
          {(d.refunds_by_method || []).length > 0 && (
            <div className="mt-2 border-t border-border pt-2 text-sm" data-testid="refunds-by-method">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Refunds by Payment Method</p>
              {d.refunds_by_method.map((r) => <div key={r.name} className="flex justify-between gap-3" data-testid={`refund-method-${r.name}`}><span>{r.name}</span><span className="mono text-rose-500 text-xs sm:text-sm text-right shrink-0">−{money(r.total)}</span></div>)}
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <ListCard title="Sales by Cashier" rows={d.sales_by_cashier} />
        <ListCard title="Expenses by Category" rows={d.expenses_by_category} />
        <Card className="p-5">
          <p className="font-head font-bold mb-3">Inventory & Receivables</p>
          <Row label="Inventory Value" value={money(d.inventory_value)} icon={Boxes} />
          <Row label="Low Stock Items" value={d.low_stock} icon={Package} />
          <Row label="Out of Stock" value={d.out_of_stock} icon={Package} />
          <Row label="Accounts Receivable" value={money(d.total_receivable)} />
          <Row label="Overdue" value={money(d.total_overdue)} danger />
          <Row label="Payments Collected" value={money(d.payments_collected)} />
        </Card>
      </div>
      </>)}
    </div>
  );
}

const Big = ({ icon: Icon, label, value, color }) => (
  <Card className="p-5"><div className="flex items-center justify-between"><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><Icon className={`w-5 h-5 ${color}`} /></div><p className="mono text-2xl font-bold mt-1">{value}</p></Card>
);
const ListCard = ({ title, rows }) => (
  <Card className="p-5"><p className="font-head font-bold mb-3">{title}</p>
    <div className="space-y-2">{rows.length ? rows.map((r) => (
      <div key={r.name} className="flex justify-between text-sm"><span className="text-muted-foreground">{r.name}</span><span className="mono font-bold">{money(r.total)}</span></div>
    )) : <p className="text-sm text-muted-foreground">No data.</p>}</div>
  </Card>
);
const Row = ({ label, value, icon: Icon, danger }) => (
  <div className="flex justify-between items-center py-1.5 text-sm"><span className="text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="w-4 h-4" />}{label}</span><span className={`mono font-bold ${danger ? "text-rose-500" : ""}`}>{value}</span></div>
);
